import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import { Box, IconButton, InputBase, Tooltip, useTheme } from '@mui/material';
import { type ChangeEvent, type KeyboardEvent, useRef, useState } from 'react';

import {
  AttachmentStrip,
  ATTACHMENT_ACCEPT_ATTR,
  attachmentGate,
  attachTooltip,
  canSendMessage,
  type EpiStagedAttachment,
} from '@verantos-dev/feature-epi-chat';

interface ProjectLandingComposerProps {
  /** Called with trimmed text when the user submits. Empty submissions are dropped. */
  onSubmit: (text: string) => void;
  /**
   * OR-6125: file-upload entry point on the landing surface. When provided, the attach
   * button is enabled and picking a file hands it back here (the parent mints a chat and
   * uploads into it). Omitted (e.g. flag off) → the button renders disabled.
   */
  onAttach?: (file: File) => void;
  /**
   * Alternative to {@link onAttach}: opens a handler (e.g. the create-concept-
   * group modal) directly instead of a native file picker. Takes precedence.
   */
  onAttachClick?: () => void;
  /** Accepted file extensions for the attach picker (e.g. ".csv,.xlsx,.xls"). */
  attachAccept?: string;
  /**
   * Files staged for the first message, rendered as cards above the input. They
   * carry their own upload status; submit is held while any is still uploading.
   */
  attachments?: EpiStagedAttachment[];
  /** Drops one staged attachment. Required for the cards to render a remove control. */
  onRemoveAttachment?: (id: string) => void;
  /** Disable input while a chat is being minted. */
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Composer shown on the landing surface before any chat exists. Submitting
 * here mints a new chat and routes the user to it — there's no live
 * session yet, so this is intentionally NOT the in-chat `ChatComposer`
 * (which is bound to streaming state, scope chips, oversight mode, etc.).
 *
 * Minimal by design — a text area and a send button, pinned to the **top**
 * of the row so the send button doesn't drift to the vertical middle as the
 * input grows to multiple lines.
 */
export const ProjectLandingComposer = ({
  onSubmit,
  onAttach,
  onAttachClick,
  attachAccept = ATTACHMENT_ACCEPT_ATTR,
  attachments,
  onRemoveAttachment,
  disabled = false,
  placeholder = 'Send a message...',
}: ProjectLandingComposerProps) => {
  const theme = useTheme();
  const [value, setValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFilePicked = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again re-fires onChange.
    event.target.value = '';
    if (file) {
      onAttach?.(file);
    }
  };

  const { atLimit } = attachmentGate(attachments);
  const canSend = canSendMessage({ text: value, attachments });

  const handleSubmit = () => {
    if (!canSend || disabled) return;
    onSubmit(value.trim());
    setValue('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box
      data-testid="project-landing-composer"
      sx={{
        // Column so staged attachments sit inside the input's own box.
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(2),
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <AttachmentStrip
        attachments={attachments ?? []}
        onRemove={onRemoveAttachment}
        testId="project-landing-composer-attachments"
        cardTestIdPrefix="project-landing-attachment-"
      />
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
        {/* Attach opens the create-concept-group modal via onAttachClick (takes
            precedence); onAttach falls back to the native file picker. */}
        <Tooltip
          title={attachTooltip({
            atLimit,
            canAttach: Boolean(onAttachClick || onAttach),
            accept: attachAccept,
          })}
        >
          <span style={{ display: 'flex', alignSelf: 'center', marginLeft: 8 }}>
            <IconButton
              size="small"
              onClick={onAttachClick ?? (() => fileInputRef.current?.click())}
              disabled={(!onAttach && !onAttachClick) || disabled || atLimit}
              aria-label="Attach a file"
              data-testid="project-landing-composer-attach"
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {!onAttachClick && (
          <input
            ref={fileInputRef}
            type="file"
            accept={attachAccept}
            hidden
            onChange={handleFilePicked}
            data-testid="project-landing-composer-file-input"
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', paddingX: 1.5 }}>
          <InputBase
            fullWidth
            multiline
            maxRows={6}
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            inputProps={{
              'aria-label': placeholder,
              'data-testid': 'project-landing-composer-input',
            }}
          />
        </Box>
        <IconButton
          aria-label="Start chat"
          data-testid="project-landing-composer-send"
          disabled={disabled || !canSend}
          onClick={handleSubmit}
          sx={{
            alignSelf: 'stretch',
            width: 48,
            borderRadius: 0,
            // Stays visually identical (filled blue) even while disabled (no
            // text yet) the `disabled` attribute still blocks the click
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { backgroundColor: 'primary.dark' },
            '&.Mui-disabled': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
            },
          }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};
